/**
 * Notifications: what happened (kind), who hears about it in the app and by
 * email (preferences), @mentions in comments, and when batched emails go out.
 * Shared by the module (which creates notifications), the API (which sends
 * the emails) and the web app (bell and settings).
 */

export const NOTIFICATION_KINDS = [
  'mention',
  'reply',
  'comment',
  'review_requested',
  'review_approved',
  'changes_requested',
  'export_done',
  'export_failed',
  'edited',
  'version_saved',
  'version_restored',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/** Preferences are per category, not per kind. */
export const NOTIFICATION_CATEGORIES = ['mentions', 'comments', 'reviews', 'changes', 'exports'] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const CATEGORY_LABELS: Record<NotificationCategory, { label: string; hint: string }> = {
  mentions: { label: 'Mentions and replies', hint: 'Someone @mentions you or replies to your comment.' },
  comments: { label: 'Comments', hint: 'New comments on projects you follow, including from review links, and on media you shared or uploaded.' },
  reviews: { label: 'Reviews', hint: 'Reviews requested, approvals and change requests on projects you follow and media you shared or uploaded.' },
  changes: { label: 'Changes', hint: 'Someone else edits, saves a version of or restores a project you follow.' },
  exports: { label: 'Exports', hint: 'Your exports finish or fail.' },
};

export function categoryOf(kind: string): NotificationCategory {
  switch (kind) {
    case 'mention':
    case 'reply':
      return 'mentions';
    case 'review_requested':
    case 'review_approved':
    case 'changes_requested':
      return 'reviews';
    case 'export_done':
    case 'export_failed':
      return 'exports';
    case 'edited':
    case 'version_saved':
    case 'version_restored':
      return 'changes';
    default:
      return 'comments';
  }
}

export const EMAIL_FREQUENCIES = ['instant', 'hourly', 'daily', 'off'] as const;
export type EmailFrequency = (typeof EMAIL_FREQUENCIES)[number];

export const EMAIL_FREQUENCY_LABELS: Record<EmailFrequency, string> = {
  instant: 'Right away (a few minutes, grouped)',
  hourly: 'Hourly summary',
  daily: 'Daily summary',
  off: 'Never',
};

/**
 * How long a notification waits before it is emailed. Even "instant" waits a
 * little: notifications read in the app meanwhile are not emailed, and a burst
 * of activity goes out as one email.
 */
export const EMAIL_DELAY_SECONDS: Record<Exclude<EmailFrequency, 'off'>, number> = {
  instant: 3 * 60,
  hourly: 60 * 60,
  daily: 24 * 60 * 60,
};

export interface NotificationPrefs {
  inApp: Record<NotificationCategory, boolean>;
  email: Record<NotificationCategory, boolean>;
  emailFrequency: EmailFrequency;
}

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  inApp: { mentions: true, comments: true, reviews: true, changes: true, exports: true },
  email: { mentions: true, comments: true, reviews: true, changes: false, exports: false },
  emailFrequency: 'instant',
};

/** Reads stored preferences ('' or anything malformed falls back to the defaults, per field). */
export function parseNotificationPrefs(json: string): NotificationPrefs {
  let raw: any = null;
  try {
    raw = json ? JSON.parse(json) : null;
  } catch {
    raw = null;
  }
  const d = DEFAULT_NOTIFICATION_PREFS;
  const flags = (src: any, def: Record<NotificationCategory, boolean>) =>
    Object.fromEntries(NOTIFICATION_CATEGORIES.map((c) => [c, typeof src?.[c] === 'boolean' ? src[c] : def[c]])) as Record<NotificationCategory, boolean>;
  return {
    inApp: flags(raw?.inApp, d.inApp),
    email: flags(raw?.email, d.email),
    emailFrequency: (EMAIL_FREQUENCIES as readonly string[]).includes(raw?.emailFrequency) ? raw.emailFrequency : d.emailFrequency,
  };
}

export function serializeNotificationPrefs(p: NotificationPrefs): string {
  return JSON.stringify(parseNotificationPrefs(JSON.stringify(p)));
}

/** Whether a notification of `kind` is shown in the app / emailed under `prefs`. */
export function wantsInApp(prefs: NotificationPrefs, kind: string): boolean {
  return prefs.inApp[categoryOf(kind)];
}
export function wantsEmail(prefs: NotificationPrefs, kind: string): boolean {
  return prefs.emailFrequency !== 'off' && prefs.email[categoryOf(kind)];
}

// ---------------------------------------------------------------------------
// Following projects
// ---------------------------------------------------------------------------

/**
 * all: every comment, review and change on the project. mute: nothing but
 * @mentions and replies in your threads. Without a row, a project's creator
 * follows it and everyone else gets only mentions and replies.
 */
export const WATCH_LEVELS = ['all', 'mute'] as const;
export type WatchLevel = (typeof WATCH_LEVELS)[number];

// ---------------------------------------------------------------------------
// Who is told
// ---------------------------------------------------------------------------

/** The stronger reason wins when one event concerns a person more than once. */
export const NOTIFICATION_PRIORITY: readonly NotificationKind[] = [
  'mention',
  'reply',
  'changes_requested',
  'review_approved',
  'review_requested',
  'comment',
  'version_restored',
  'version_saved',
  'edited',
  'export_failed',
  'export_done',
];

/** Records why `userId` hears about an event, keeping the strongest reason. */
export function addNotificationReason(reasons: Map<string, NotificationKind>, userId: string, kind: NotificationKind) {
  if (!userId) return;
  const had = reasons.get(userId);
  if (!had || NOTIFICATION_PRIORITY.indexOf(kind) < NOTIFICATION_PRIORITY.indexOf(had)) reasons.set(userId, kind);
}

/**
 * People around an asset shared for review. Media has no follow of its own:
 * whoever shared it (made the link), uploaded it, started its review round,
 * and the members who wrote in the link's conversation hear about it.
 */
export interface AssetAudience {
  /** Whoever made the link the activity came through (every link of the asset, for a member's decision). */
  linkCreatorUserIds: readonly string[];
  uploaderUserId: string;
  reviewRequesterUserId: string;
  participantUserIds: readonly string[];
}

/**
 * Who hears about a comment or decision on a shared asset, and why:
 * @mentions (from members only; guests cannot see who is in the
 * organization), the others in a thread that was answered, then the asset's
 * audience. The actor is never told about their own action. The module still
 * checks each person can see the asset and wants the notification.
 */
export function assetActivityRecipients(
  ev: { kind: 'comment' | 'review_approved' | 'changes_requested'; actorUserId: string; mentionedUserIds: readonly string[]; threadUserIds: readonly string[] },
  audience: AssetAudience,
): Map<string, NotificationKind> {
  const reasons = new Map<string, NotificationKind>();
  if (ev.actorUserId) for (const id of ev.mentionedUserIds) addNotificationReason(reasons, id, 'mention');
  for (const id of ev.threadUserIds) addNotificationReason(reasons, id, 'reply');
  for (const id of [...audience.linkCreatorUserIds, audience.uploaderUserId, audience.reviewRequesterUserId, ...audience.participantUserIds]) addNotificationReason(reasons, id, ev.kind);
  if (ev.actorUserId) reasons.delete(ev.actorUserId);
  return reasons;
}

// ---------------------------------------------------------------------------
// Mentions
// ---------------------------------------------------------------------------

/**
 * A mention is stored in a comment as `@[Display name](userId)` and shown as
 * "@Display name". The name is only for display; the user id decides who is
 * notified.
 */
export const MENTION_PATTERN = /@\[([^\]\n]{1,80})\]\(([A-Za-z0-9_-]{1,64})\)/g;

export function mentionToken(name: string, userId: string): string {
  const clean = name.replace(/[\[\]\n()]/g, '').trim().slice(0, 80) || 'someone';
  return `@[${clean}](${userId})`;
}

/** User ids mentioned in a comment, each once, in order. */
export function mentionedUserIds(body: string): string[] {
  const out: string[] = [];
  for (const m of body.matchAll(MENTION_PATTERN)) if (!out.includes(m[2])) out.push(m[2]);
  return out;
}

export type MentionPart = { text: string } | { mention: string; userId: string };

/** Splits a comment into text and mentions, for rendering. */
export function splitMentions(body: string): MentionPart[] {
  const parts: MentionPart[] = [];
  let last = 0;
  for (const m of body.matchAll(MENTION_PATTERN)) {
    const i = m.index ?? 0;
    if (i > last) parts.push({ text: body.slice(last, i) });
    parts.push({ mention: m[1], userId: m[2] });
    last = i + m[0].length;
  }
  if (last < body.length) parts.push({ text: body.slice(last) });
  return parts;
}

/** The comment as people read it ("@Name" instead of the stored token). */
export function plainMentions(body: string): string {
  return body.replace(MENTION_PATTERN, (_m, name: string) => `@${name}`);
}

/** A short single-line preview of a comment. */
export function excerpt(body: string, max = 160): string {
  const s = plainMentions(body).replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s;
}

// ---------------------------------------------------------------------------
// Links and emails
// ---------------------------------------------------------------------------

/**
 * In-app path a notification opens: the project at the comment, or for a
 * shared asset the media library with its share dialog (and the comment's
 * conversation) open.
 */
export function notificationPath(n: { projectId: string; commentId: string; orgId: string; kind: string; assetId?: string }): string {
  if (n.assetId && n.orgId) return `/orgs/${n.orgId}/assets?share=${encodeURIComponent(n.assetId)}${n.commentId ? `&comment=${encodeURIComponent(n.commentId)}` : ''}`;
  if (n.projectId) return `/p/${n.projectId}${n.commentId ? `?comment=${encodeURIComponent(n.commentId)}` : ''}`;
  if (n.kind.startsWith('export_') && n.orgId) return `/orgs/${n.orgId}/exports`;
  return n.orgId ? `/orgs/${n.orgId}` : '/';
}

/** One entry of a notification email (the payload of an email_outbox row). */
export interface NotificationEmailItem {
  kind: string;
  title: string;
  body: string;
  actorName: string;
  projectName: string;
  path: string;
  at: number;
}

export interface NotificationEmailPayload {
  userId: string;
  name: string;
  items: NotificationEmailItem[];
  /** Notifications not listed (the email shows the newest). */
  more: number;
}

/** Most notifications listed in one email. */
export const EMAIL_MAX_ITEMS = 25;

/**
 * Someone editing a project notifies its followers once per editing session:
 * when a different person edits, or after this long without edits.
 */
export const EDIT_SESSION_GAP_SECONDS = 2 * 60 * 60;

/** Notifications kept per person, and for how long. */
export const NOTIFICATIONS_KEPT = 300;
export const NOTIFICATION_RETENTION_DAYS = 90;
